<template>
  <modal :show-close="modalConfig.showClose"
         :wrapper-classes="modalConfig.wrapperClasses"
         @close="closeModal()">
    <h2 slot="header" class="modal-title">
      digital voucher
    </h2>
    <h3 slot="subHeader">
      configure a digital voucher that will send out to your recipients and display in their loyalty rewards wallet
    </h3>
    <div slot="body">
      <div class="column">
        <label>offer for customer</label>
        <input
          type="text"
          class="item-name-input"
          :class="{ error: !digitalVoucherDraftState.item_name }"
          v-model="digitalVoucherDraftState.item_name"
          maxlength="50"
          data-cy="digital-voucher-name-input"
        />
        <span class="form-field-notice">max 50 characters</span>
      </div>

      <div class="column">
        <label>offers per customer</label>
        <select v-model="digitalVoucherDraftState.max_redemptions" data-cy="digital-voucher-max-redemptions-select">
          <option value="1">1</option>
          <option value="2">2</option>
          <option value="3">3</option>
          <option value="4">4</option>
          <option value="5">5</option>
          <option value="10">10</option>
          <option value="-1">unlimited</option>
        </select>
      </div>

      <div class="column">
        <label>expiration date</label>
        <span class="form-field-notice">expires at 11:59 PM</span>
        <sb-date
          :flatpickr="flatPickrConfig"
          :include="['date']"
          placeholder="-"
          format="E, MMM d, y"
          mode="single"
          :value="expirationDate"
          :class="{ error: !digitalVoucherDraftState.expiration_date}"
          class="expiration-date"
          @update:value="changedExpiration"
          data-cy="digital-voucher-expiration-date-input" />
      </div>

      <div v-if="!loadingDisplayImage" class="column">
        <label>display image</label>
        <span class="form-field-notice">displays on offers page</span>
        <span v-if="displayImageError" class="form-field-notice error">{{ displayImageError }}</span>
        <template v-if="!displayImage">
          <a v-file-upload:png,gif,jpeg="changedDisplayImage" style="margin-bottom:3px" data-cy="digital-voucher-display-image-button">
            <fai :icon="['fas', 'camera']" />
          </a>
        </template>
        <template v-if="displayImage">
            <img :src="displayImage.thumb" alt="display image" />
          <span class="remove-btn" @click="changedDisplayImage()" data-cy="digital-voucher-display-image-remove-button">remove</span>
        </template>
      </div>

      <sb-loading-icon v-if="loadingDisplayImage" />

      <div v-if="!loadingVoucherImage" class="column">
        <label>voucher image</label>
        <span class="form-field-notice">displays on offer redemption</span>
        <span v-if="voucherImageError" class="form-field-notice error">{{ voucherImageError }}</span>
        <template v-if="!voucherImage">
          <a v-file-upload:png,gif,jpeg="changedVoucherImage" style="margin-bottom:3px" data-cy="digital-voucher-voucher-image-button">
            <fai :icon="['fas', 'camera']" />
          </a>
        </template>
        <template v-if="voucherImage">
            <img :src="voucherImage.thumb" alt="voucher image" />
          <span class="remove-btn" @click="changedVoucherImage()" data-cy="digital-voucher-voucher-image-remove-button">remove</span>
        </template>
      </div>

      <sb-loading-icon v-if="loadingVoucherImage" />
    </div>
    <div slot="action" class="modal-action-click">
      <button class="button larger done-button" :disabled="isDoneButtonDisabled" @click="clickedDone()" data-cy="digital-voucher-done-button">done</button>
      <span class="error">{{ error }}</span>
    </div>
  </modal>
</template>

<script>
import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { attachmentAPI } from '../../../services/api/attachmentAPI.js';
import { rewardAPI } from "../../../services/api/rewardAPI.js";
import Modal from "../../shared/Modal.vue";
import { removeTimeZoneAndGetDateUtil } from "../../../utilities";
import _ from "lodash";
import { mapGetters } from "vuex";

export default {
  props: {
    campaign: Object,
    extension: Object
  },

  name: "digital-voucher-modal",

  components: {
    Modal
  },

  data() {
    return {
      digitalVoucherDraftState: null,
      displayImage: null,
      error: null,
      expirationDate: null,
      loadingDisplayImage: false,
      loadingVoucherImage: false,
      modalConfig: {
        showClose: true,
        wrapperClasses: ["extensions-modal","digital-voucher-modal"]
      },
      voucherImage: null,
      maxAttachmentSize: 1024 * 1024 * 10, // 10 mb
      displayImageError: null,
      voucherImageError: null
    }
  },

  async created() {
    this.digitalVoucherDraftState = _.cloneDeep(this.extension.payload);
    this.digitalVoucherDraftState.type = null;
    this.expirationDate = !!this.digitalVoucherDraftState.expiration_date ? utcToZonedTime(this.digitalVoucherDraftState.expiration_date, this.currentMerchant.merchant_timezone) : null;
    await this.refreshAttachments()
  },

  methods: {
    async changedDisplayImage(file) {
      this.loadingDisplayImage = true;
      this.displayImageError = null;

      if(!file && this.displayImage) {
        try {
          await attachmentAPI.delete(this.displayImage.uuid);
          this.digitalVoucherDraftState.has_preload_image = false;
        }catch(e){
          // TODO: Handle error here
          this.error = e
        }
      } else {
        const error = this.validateFile(file)
        if(error) {
          this.displayImageError = error;
          this.loadingDisplayImage = false;
          return;
        }

        try{
          await attachmentAPI.create(this.digitalVoucherDraftState.id, 'preload', 'Reward', file);
          this.digitalVoucherDraftState.has_preload_image = true;
        }catch(e){
          // TODO: Handle error here
          this.error = e
        }
      }

      await this.refreshAttachments();
      this.loadingDisplayImage = false;
    },

    changedExpiration(selectedDate) {
      this.expirationDate = selectedDate;
      const formattedDate = selectedDate.length === 0 ? null : format(selectedDate, 'y-MM-dd');
      this.digitalVoucherDraftState.expiration_date = formattedDate;
      this.digitalVoucherDraftState.expiration_date_local = formattedDate;
      return;
    },

    validateFile(file) {
      if(file.size >= this.maxAttachmentSize) {
        return "issue uploading image: file size must be less than 10 mb"
      }
    },

    async changedVoucherImage(file) {
      this.loadingVoucherImage = true;
      this.voucherImageError = null;

      if(!file && this.voucherImage) {
        try{
          await attachmentAPI.delete(this.voucherImage.uuid);
          this.digitalVoucherDraftState.has_coupon_image = false;
        }catch(e){
          // TODO: Handle error here
          this.error = e
        }
      } else {
        const error = this.validateFile(file)
        if(error) {
          this.voucherImageError = error;
          this.loadingVoucherImage = false;
          return;
        }

        try {
          await attachmentAPI.create(this.digitalVoucherDraftState.id, 'coupon', 'Reward', file);
          this.digitalVoucherDraftState.has_coupon_image = true;
        }catch(e){
          // TODO: Handle error here
          this.error = e
        }
      }

      await this.refreshAttachments();
      this.loadingVoucherImage = false;
    },

    async clickedDone() {
      try{
        const { reward } = await rewardAPI.patchRewards(this.digitalVoucherDraftState.id, {
          owner_id: this.campaign.id,
          owner_type: 'Campaign',
          reward: this.digitalVoucherDraftState
        });

        await this.$store.dispatch('UPDATE_VALUES', { reward });
        await this.$store.dispatch('VALIDATE', ['reward']);
        this.extension.payload = this.digitalVoucherDraftState;
        this.$emit("closeModal");
      }catch(e){
        // TODO: Handle error here
        this.error = e
      }
    },

    closeModal() {
      this.$emit("closeModal")
    },

    async refreshAttachments() {
      this.displayImage = null;
      this.voucherImage = null;

      if(this.digitalVoucherDraftState.id !== null) {
        const attachments = await attachmentAPI.forReward(this.digitalVoucherDraftState.id, this.campaign.id, "Campaign")

        if(Array.isArray(attachments)) {
          const preloads = attachments.filter((attachment) => attachment.label === "preload");
          if(preloads.length > 0) {
            this.displayImage = preloads[preloads.length - 1];
            this.digitalVoucherDraftState.has_preload_image = true;
          }

          const coupons = attachments.filter((attachment) => attachment.label === "coupon");
          if(coupons.length > 0) {
            this.voucherImage = coupons[coupons.length - 1];
            this.digitalVoucherDraftState.has_coupon_image = true;
          }
        }
      }
    },
  },

  computed: {
    flatPickrConfig() {
      return {
        minDate: new Date()
      }
    },
    isDoneButtonDisabled() {
      return !this.digitalVoucherDraftState.item_name || !this.digitalVoucherDraftState.expiration_date
    },
    ...mapGetters(["currentMerchant"])
  }
}
</script>

<style scoped>
.digital-voucher,
.digital-voucher-modal .attachment-container,
.digital-voucher-modal .column {
  align-items: start;
  display: flex;
  flex-direction: column;
  width: 100%;
}

.digital-voucher-modal .column {
  margin-bottom: 15px;
}

.digital-voucher-modal select,
.digital-voucher-modal input {
  font-size: 18px;
}

.digital-voucher-modal a {
  font-family: "AileronLight",sans-serif;
  margin-bottom: 5px;
}

.digital-voucher-modal svg {
  height: 4rem;
  width: 4rem;
}

.digital-voucher-modal img {
  border: 1px solid #128ff9;
  height: 75px;
  margin-top: 8px;
}

.digital-voucher-modal span.remove-btn {
  color: #a4a4a4;
  cursor: pointer;
  font-size: .9em;
  margin-left: 15px;
}

.digital-voucher-modal .remove-btn:hover{
  color: #f91253;
}

.digital-voucher-modal input,
.digital-voucher-modal select,
.digital-voucher-modal .expiration-date {
  margin-bottom: 0;
  width: 100%;
}

.digital-voucher-modal .expiration-date {
  border: 1px solid #128ff9;
  border-radius: 8px;
}

.digital-voucher-modal .expiration-date >>> .label-container {
  color: #000;
}

.digital-voucher-modal .error {
  border-color: #f91253;
}

.digital-voucher-modal .sb-loading-icon {
  margin-left: 15px;
}

.sb-loading-icon >>> svg {
  fill: #128ff9;
}
</style>
